import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * HybridTabBar — the primary-tabs + "More ▼" reorderable dropdown that
 * replaces the flat horizontal scroll strip on the vessel Dashboard.
 *
 * Rationale (Feb 8, 2026): the old strip had 15-16 tabs and forced captains
 * to scroll horizontally to reach the categories they cared about. The
 * hybrid layout pins the 3-4 most-used views (Overview, Cruising Log,
 * Contacts, Activity) and stashes everything else under "More ▼" — where
 * each row has ↑/↓ buttons to reorder. Order persists per-user via
 * PATCH /api/auth/me { tab_order: [...] }.
 *
 * Up/down buttons chosen over HTML5 drag-and-drop because DnD is
 * unreliable on touch devices without a polyfill (react-dnd's Touch
 * backend has known issues on iOS Safari 17+). The two-button reorder is
 * unambiguous on every device and works with switch-control / VoiceOver.
 *
 * Props:
 *   primary      — array of tab labels that are always visible as pinned
 *                  buttons at the front of the bar
 *   all          — full ordered list of category labels for this vessel
 *   currentTab   — currently active tab label
 *   onTabChange  — fn(label) → void
 *   userTabOrder — user's persisted preferred order for "More" items
 *                  (array of labels). Missing labels are appended in
 *                  `all`-order at the end.
 *   onOrderChange — fn(newOrderArray) → void, called AFTER a successful
 *                   PATCH so parent can update local state
 *   vesselName   — display-only, rendered on the left of the strip
 */
export default function HybridTabBar({
    primary = [],
    all = [],
    currentTab,
    onTabChange,
    userTabOrder = [],
    onOrderChange,
    vesselName = "",
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Items eligible for the "More" dropdown = every category that is NOT
    // pinned as a primary tab.
    const moreEligible = useMemo(
        () => all.filter((c) => !primary.includes(c)),
        [all, primary]
    );

    // Effective order for the More dropdown: user's preferred sequence
    // first (filtered to categories that actually exist for this vessel),
    // then any new categories the user hasn't seen yet, appended in the
    // vessel's default order.
    const orderedMore = useMemo(() => {
        const eligibleSet = new Set(moreEligible);
        const preferred = userTabOrder.filter((c) => eligibleSet.has(c));
        const preferredSet = new Set(preferred);
        const unseen = moreEligible.filter((c) => !preferredSet.has(c));
        return [...preferred, ...unseen];
    }, [moreEligible, userTabOrder]);

    // Close the menu when clicking outside or pressing Escape.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDoc = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setMenuOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [menuOpen]);

    const persistOrder = async (newOrder) => {
        // Save the full ordered array (including seen items) so the sort is
        // stable across new vessels/categories added later.
        try {
            await api.patch("/auth/me", { tab_order: newOrder });
            onOrderChange && onOrderChange(newOrder);
        } catch (e) {
            // Non-fatal — local reorder still visible until page refresh.
            console.warn("[hybrid-tabs] persist order failed", e?.response?.status);
        }
    };

    const move = (label, dir) => {
        const idx = orderedMore.indexOf(label);
        const target = idx + dir;
        if (idx < 0 || target < 0 || target >= orderedMore.length) return;
        const next = [...orderedMore];
        [next[idx], next[target]] = [next[target], next[idx]];
        persistOrder(next);
    };

    const isMoreActive = currentTab && !primary.includes(currentTab);

    return (
        <div
            data-testid="hybrid-tab-bar"
            style={{
                display: "flex", alignItems: "center", gap: 4,
                flexWrap: "wrap", padding: "4px 0",
            }}
        >
            {vesselName && (
                <span
                    className="bm-display"
                    data-testid="hybrid-tab-vessel-name"
                    style={{ marginRight: 18, fontSize: 16 }}
                >
                    {vesselName}
                </span>
            )}

            {/* Pinned primary tabs — always visible */}
            {primary.map((c) => (
                <button
                    key={c}
                    data-testid={`tab-${slug(c)}`}
                    className="bm-tab"
                    data-active={currentTab === c}
                    onClick={() => onTabChange(c)}
                    type="button"
                >
                    {c}
                </button>
            ))}

            {/* More ▼ */}
            {orderedMore.length > 0 && (
                <div ref={menuRef} style={{ position: "relative" }}>
                    <button
                        type="button"
                        data-testid="hybrid-tab-more-btn"
                        className="bm-tab"
                        data-active={isMoreActive}
                        aria-haspopup="listbox"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((v) => !v)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                        {isMoreActive ? currentTab : "More"}
                        {menuOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>

                    {menuOpen && (
                        <div
                            data-testid="hybrid-tab-more-menu"
                            role="listbox"
                            style={{
                                position: "absolute", top: "calc(100% + 6px)", left: 0,
                                minWidth: 260, maxWidth: 320,
                                maxHeight: "min(70vh, 480px)", overflowY: "auto",
                                background: "var(--bm-bg-1, #0c1828)",
                                border: "1px solid var(--bm-border)",
                                borderRadius: 10,
                                boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                                zIndex: 20,
                                padding: 6,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10, letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    color: "var(--bm-text-muted)",
                                    padding: "8px 10px 6px",
                                }}
                            >
                                Categories · tap ↑↓ to reorder
                            </div>
                            {orderedMore.map((c, idx) => {
                                const active = currentTab === c;
                                return (
                                    <div
                                        key={c}
                                        data-testid={`hybrid-tab-more-row-${slug(c)}`}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            padding: "4px 4px 4px 8px",
                                            borderRadius: 6,
                                            background: active ? "rgba(212,181,114,0.08)" : "transparent",
                                        }}
                                    >
                                        <button
                                            type="button"
                                            data-testid={`hybrid-tab-select-${slug(c)}`}
                                            onClick={() => { onTabChange(c); setMenuOpen(false); }}
                                            style={{
                                                flex: 1,
                                                background: "transparent",
                                                border: "none",
                                                color: active ? "var(--bm-gold)" : "var(--bm-text, #eee)",
                                                textAlign: "left",
                                                padding: "6px 4px",
                                                fontSize: 14,
                                                cursor: "pointer",
                                                display: "inline-flex", alignItems: "center", gap: 8,
                                            }}
                                        >
                                            {active && <Check size={13} />}
                                            <span>{c}</span>
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`hybrid-tab-move-up-${slug(c)}`}
                                            aria-label={`Move ${c} up`}
                                            disabled={idx === 0}
                                            onClick={(e) => { e.stopPropagation(); move(c, -1); }}
                                            className="bm-btn bm-btn-ghost"
                                            style={{ padding: "4px 6px", opacity: idx === 0 ? 0.3 : 1 }}
                                        >
                                            <ArrowUp size={12} />
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`hybrid-tab-move-down-${slug(c)}`}
                                            aria-label={`Move ${c} down`}
                                            disabled={idx === orderedMore.length - 1}
                                            onClick={(e) => { e.stopPropagation(); move(c, 1); }}
                                            className="bm-btn bm-btn-ghost"
                                            style={{ padding: "4px 6px", opacity: idx === orderedMore.length - 1 ? 0.3 : 1 }}
                                        >
                                            <ArrowDown size={12} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
